import { ceilToNiceStep } from "./niceStep";

/*
 * Clock notation shared by the timeline ruler, the playback readout, the chart time axis and the
 * value popup. One surface picks one form from the latest time it can show (its media duration,
 * or its viewport end when it has no media): `MM:SS`, `H:MM:SS`, then elapsed days `Nd HH:MM:SS`.
 * Days are elapsed time, not calendar dates.
 */

export const SECONDS_PER_HOUR = 3600;
export const SECONDS_PER_DAY = 86400;

export type ClockScale = "minutes" | "hours" | "days";

export function clockScaleFor(latestSeconds: number): ClockScale {
  if (!Number.isFinite(latestSeconds)) return "minutes";
  if (latestSeconds >= SECONDS_PER_DAY) return "days";
  return latestSeconds >= SECONDS_PER_HOUR ? "hours" : "minutes";
}

/** The form for a span that may reach before zero: its largest magnitude picks it. */
export function clockScaleForSpan(start: number, end: number): ClockScale {
  return clockScaleFor(Math.max(Math.abs(start), Math.abs(end)));
}

/**
 * `format` applied to the magnitude, with a leading minus when the time is before zero and does
 * not round to zero. Chart time can run before zero (samples relative to an event); media time
 * cannot, so playback surfaces format without it.
 */
export function formatSignedClock(seconds: number, format: (magnitude: number) => string): string {
  const text = format(Math.abs(seconds));
  return seconds < 0 && /[1-9]/.test(text) ? `-${text}` : text;
}

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * `seconds` in the scale's form with `fractionDigits` decimals. Rounding happens once at the
 * finest unit, so 59.96 s at one decimal carries into `01:00.0`. Non-finite and negative input
 * renders as zero: a display formatter must stay stable, never `NaN:NaN`.
 */
export function formatClockTime(
  seconds: number,
  fractionDigits: number,
  scale: ClockScale,
): string {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const unit = 10 ** fractionDigits;
  const scaled = Math.round(safe * unit);
  const whole = Math.floor(scaled / unit);
  const fraction = scaled - whole * unit;
  const secondsPart = pad(whole % 60);
  // The scale is a floor: a value that rounds past it (59:59.96 → 1:00:00.0) takes the next form.
  const form: ClockScale =
    scale === "days" || whole >= SECONDS_PER_DAY
      ? "days"
      : scale === "hours" || whole >= SECONDS_PER_HOUR
        ? "hours"
        : "minutes";
  const clock =
    form === "days"
      ? `${Math.floor(whole / SECONDS_PER_DAY)}d ${pad(Math.floor((whole % SECONDS_PER_DAY) / SECONDS_PER_HOUR))}:${pad(Math.floor((whole % SECONDS_PER_HOUR) / 60))}:${secondsPart}`
      : form === "hours"
        ? `${Math.floor(whole / SECONDS_PER_HOUR)}:${pad(Math.floor((whole % SECONDS_PER_HOUR) / 60))}:${secondsPart}`
        : `${pad(Math.floor(whole / 60))}:${secondsPart}`;
  return fractionDigits === 0
    ? clock
    : `${clock}.${String(fraction).padStart(fractionDigits, "0")}`;
}

/**
 * Tick spacings on clock units: {1, 2, 5}×10^k below ten seconds, then 15 and 30 s, 1·2·5·10·15·30
 * min, 1·2·3·6·12 h and 1·2·7·14 d. Decimal steps past ten seconds (50 s, 500 s) would put labels
 * between minutes.
 */
export const CLOCK_STEPS: readonly number[] = [
  0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900,
  1800, 3600, 7200, 10800, 21600, 43200, 86400, 172800, 604800, 1209600,
];
const LONGEST_CLOCK_STEP = CLOCK_STEPS[CLOCK_STEPS.length - 1]!;

/** The smallest clock step not finer than `rough`; beyond two weeks, whole nice numbers of days. */
export function ceilToClockStep(rough: number): number {
  if (!(rough > 0)) return CLOCK_STEPS[0]!;
  const step = CLOCK_STEPS.find((candidate) => candidate >= rough * (1 - 1e-9));
  return step ?? ceilToNiceStep(rough / SECONDS_PER_DAY) * SECONDS_PER_DAY;
}

export function finerClockStep(step: number): number | undefined {
  const index = CLOCK_STEPS.findIndex((candidate) => candidate >= step * (1 - 1e-9));
  if (index > 0) return CLOCK_STEPS[index - 1];
  return step > LONGEST_CLOCK_STEP ? LONGEST_CLOCK_STEP : undefined;
}

export function coarserClockStep(step: number): number {
  return ceilToClockStep(step * (1 + 1e-6));
}

/**
 * Minor spacing under a major step: the clock step that splits it into a whole count closest to
 * five (2–7 parts, fewer on a tie), so minor marks also land on clock units.
 */
export function clockMinorStep(major: number): number {
  let best: number | undefined;
  let bestDistance = Infinity;
  for (const candidate of CLOCK_STEPS) {
    if (candidate >= major) break;
    const parts = major / candidate;
    if (Math.abs(parts - Math.round(parts)) > 1e-6 || parts < 2 || parts > 7) continue;
    const distance = Math.abs(parts - 5);
    if (distance < bestDistance || (distance === bestDistance && parts < major / best!)) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best ?? major / 5;
}

/** Decimal places that resolve one step, capped at milliseconds. */
export function clockStepDecimals(step: number): number {
  if (!(step > 0) || step >= 1) return 0;
  return Math.min(3, Math.ceil(-Math.log10(step) - 1e-9));
}

/**
 * A tick label at `step` spacing. Day-scale labels drop the places the step keeps at zero: `2d`
 * for day steps and `2d 14:30` for minute steps; seconds appear only below a minute.
 */
export function formatClockTick(seconds: number, step: number, scale: ClockScale): string {
  if (scale === "days" && step >= SECONDS_PER_DAY) {
    return `${Math.round(Math.max(0, seconds) / SECONDS_PER_DAY)}d`;
  }
  if (scale === "days" && step >= 60) {
    const minutes = Math.round(Math.max(0, seconds) / 60);
    const days = Math.floor(minutes / 1440);
    return `${days}d ${pad(Math.floor((minutes % 1440) / 60))}:${pad(minutes % 60)}`;
  }
  return formatClockTime(seconds, clockStepDecimals(step), scale);
}
